import { createApp } from 'vue'
import Antd from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import './styles/search.scss'
import App from './App.vue'

/**
 * Vue Domain Search SPA Entry Point
 * 
 * Finds all shortcode mount points on the page and creates
 * independent Vue app instances for each.
 * 
 * Edge Cases Handled:
 * - Existing Vue instance conflicts (version detection)
 * - Bundle load failures (try-catch wrapper)
 * - Multiple instances on same page (independent apps)
 */

// Detect existing Vue instance conflicts
function checkVueConflicts(): void {
  // Check if Vue is already globally available (potential conflict)
  if ((window as any).Vue) {
    const existingVersion = (window as any).Vue.version || 'unknown'
    console.warn(
      '[Conflated Domains] Existing Vue instance detected (version: ' + existingVersion + '). ' +
      'This may cause conflicts. The plugin will use its own bundled Vue instance.'
    )
  }
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApps)
} else {
  initializeApps()
}

function initializeApps() {
  // Check for Vue version conflicts
  checkVueConflicts()
  
  // Find all domain search shortcode mount points (div elements only, not script tags)
  const mountPoints = document.querySelectorAll('div[id^="conflated-domains-search-"]')
  
  if (mountPoints.length === 0) {
    console.warn('[Conflated Domains] No domain search shortcode mount points found')
    return
  }

  console.log(`[Conflated Domains] Found ${mountPoints.length} domain search shortcode(s)`)

  // Create independent Vue app for each mount point
  mountPoints.forEach((element) => {
    try {
      const mountElementId = element.id
      
      // Create app instance
      const app = createApp(App, {
        mountElementId
      })

      // Register Ant Design Vue
      app.use(Antd)

      // Mount to specific element
      app.mount(`#${mountElementId}`)

      console.log(`[Conflated Domains] Vue app mounted to #${mountElementId}`)
    } catch (error) {
      console.error(`[Conflated Domains] Failed to mount Vue app to #${element.id}:`, error)
      
      // Display user-friendly error message in mount point
      element.innerHTML = `
        <div style="padding: 1rem; background: #fee; border: 1px solid #fcc; border-radius: 4px; color: #c00;">
          <strong>Domain Search Unavailable</strong>
          <p>Unable to load the domain search interface. Please refresh the page or contact support if the problem persists.</p>
        </div>
      `
    }
  })
}
